/**
 * ActiveChallengePanel — right-rail brand-gamification slot.
 *
 * Per design spec: placeholder mirrors the wireframe layout exactly so the
 * empty state reads as intentional, not coming-soon. When a real sponsor
 * activation exists, the same shell is reused with live data.
 *
 * Rendered in the right rail only when the creator is OFFLINE. When live, the
 * right rail is occupied by ChatPanel instead — the two never co-render
 * (StreamPanel gates them on isLive).
 */

import React from "react";
import { FaTrophy, FaCheckCircle, FaCircle, FaUsers } from "react-icons/fa";

interface ChallengeStep {
    label: string;
    /** "BRAND" | "GAME" | "SOCIAL" — drives the right-side tag color */
    tag: "BRAND" | "GAME" | "SOCIAL";
    done?: boolean;
}

interface ChallengeData {
    sponsorName: string;
    sponsorSubtitle: string;
    expiresLabel: string;
    heroImageUrl?: string;
    heroHeadline: string;
    heroSubcopy: string;
    steps: ChallengeStep[];
    rewardLabel: string;
    rewardCta: string;
    extraSponsors?: Array<{ name: string; sub: string }>;
}

interface Props {
    hubName?: string;
    challenge?: ChallengeData | null;
}

/*
 * Placeholder content shown when no real activation is live. The wireframe
 * (gs://wireframes-devourplay/couragejd/) showed a Chipotle-style scavenger
 * hunt; we mirror its STRUCTURE with generic copy so creators + viewers see
 * what the slot becomes once a sponsor activates.
 */
const PLACEHOLDER_CHALLENGE: ChallengeData = {
    sponsorName: "Sponsor Challenge",
    sponsorSubtitle: "Example",
    expiresLabel: "—",
    heroHeadline: "Brand challenges live here",
    heroSubcopy: "Complete objectives to win",
    steps: [
        { label: "Visit the sponsor", tag: "BRAND" },
        { label: "Discover the offer", tag: "BRAND" },
        { label: "In-game objective", tag: "GAME" },
        { label: "Complete all to claim", tag: "BRAND" },
    ],
    rewardLabel: "COMPLETE ALL",
    rewardCta: "Sample reward + 500 XP",
    extraSponsors: [
        { name: "OPEN SLOT", sub: "Leaderboard" },
        { name: "OPEN SLOT", sub: "Bingo" },
    ],
};

export const ActiveChallengePanel: React.FC<Props> = ({ hubName, challenge }) => {
    /*
     * Dev-only placeholder fallback. With no real activation, render the
     * scavenger-hunt shell on dev / staging / Mirror5 so the offline right-rail
     * slot is demoable; production stays empty until a real `challenge` is
     * passed (the "Open Slot" state reads as broken in prod).
     */
    const data = challenge ?? (import.meta.env.VITE_ENVIRONMENT !== "production" ? PLACEHOLDER_CHALLENGE : null);
    if (!data) return null;
    const isPlaceholder = !challenge;

    return (
        <aside className="hcs-challenge" aria-label={isPlaceholder ? "Active challenge placeholder" : "Active challenge"}>
            <header className="hcs-challenge__header">
                <span className="hcs-challenge__header-icon"><FaTrophy /></span>
                <div className="hcs-challenge__header-meta">
                    <div className="hcs-challenge__header-name">{data.sponsorName}</div>
                    <div className="hcs-challenge__header-sub">{data.sponsorSubtitle}</div>
                </div>
                <div className="hcs-challenge__header-expires">{data.expiresLabel}</div>
            </header>

            <div className="hcs-challenge__hero">
                {data.heroImageUrl
                    ? <img src={data.heroImageUrl} alt="" />
                    : <div className="hcs-challenge__hero-placeholder">
                        <FaTrophy />
                    </div>
                }
                <div className="hcs-challenge__hero-overlay">
                    <div className="hcs-challenge__hero-headline">{data.heroHeadline}</div>
                    <div className="hcs-challenge__hero-subcopy">{data.heroSubcopy}</div>
                </div>
            </div>

            <ul className="hcs-challenge__steps">
                {data.steps.map((step, i) =>
                    <li key={i} className={`hcs-challenge__step${step.done ? " is-done" : ""}`}>
                        <span className="hcs-challenge__step-check">
                            {step.done ? <FaCheckCircle /> : <FaCircle />}
                        </span>
                        <span className="hcs-challenge__step-label">{step.label}</span>
                        <span className={`hcs-challenge__step-tag hcs-challenge__step-tag--${step.tag.toLowerCase()}`}>{step.tag}</span>
                    </li>)}
            </ul>

            <div className="hcs-challenge__reward">
                <div className="hcs-challenge__reward-label">{data.rewardLabel}</div>
                <div className="hcs-challenge__reward-cta">{data.rewardCta}</div>
            </div>

            {data.extraSponsors && data.extraSponsors.length > 0 &&
                <div className="hcs-challenge__more">
                    <div className="hcs-challenge__more-label">MORE SPONSOR EXPERIENCES</div>
                    <div className="hcs-challenge__more-row">
                        {data.extraSponsors.map((s, i) =>
                            <div key={i} className="hcs-challenge__more-card">
                                <span className="hcs-challenge__more-card-icon"><FaUsers /></span>
                                <div>
                                    <div className="hcs-challenge__more-card-name">{s.name}</div>
                                    <div className="hcs-challenge__more-card-sub">{s.sub}</div>
                                </div>
                            </div>)}
                    </div>
                </div>
            }

        </aside>
    );
};
